import React, { useEffect } from "react";
import Loader from "../generic/Loader";
import { useFetchMeGlobal } from "../../hooks/userHooks";
import {
  isGenericDashboardHost,
  getRegionAdminUrl,
  getScopedRegionOverride,
  setScopedRegionOverride,
  clearScopedRegionOverride,
} from "../../helpers/siteHelpers";
import { resolveRegionLanding } from "../../helpers/regionLanding";

interface RegionLandingGuardProps {
  children: React.ReactNode;
}

const wrapStyle: React.CSSProperties = {
  minHeight: "100vh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "24px",
  background: "#f5f6f8",
};

const cardStyle: React.CSSProperties = {
  maxWidth: 440,
  width: "100%",
  background: "#fff",
  borderRadius: 12,
  boxShadow: "0 8px 30px rgba(0,0,0,0.08)",
  padding: "32px",
  textAlign: "center",
  fontFamily: "system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
};

const buttonStyle: React.CSSProperties = {
  display: "block",
  width: "100%",
  margin: "8px 0",
  padding: "12px 16px",
  borderRadius: 8,
  border: "1px solid #d0d4da",
  background: "#fff",
  color: "rgb(var(--pcg-content))",
  fontSize: 15,
  fontWeight: 600,
  cursor: "pointer",
};

const regionLabel = (region: string): string => region.toUpperCase();

/**
 * On the generic admin dashboard host only, routes the authenticated user to
 * their regional portal (or offers a chooser / explains when no portal applies).
 * On every other host this is a transparent pass-through — regional portals are
 * untouched.
 */
const RegionLandingGuard = ({ children }: RegionLandingGuardProps) => {
  const onGenericHost = isGenericDashboardHost();
  const { data: user, isLoading, isError } = useFetchMeGlobal(onGenericHost);

  const { accessibleRegions, targetRegion, scopedRegion } = resolveRegionLanding(
    user?.region,
    user?.regions
  );

  // Dev/test/unlinked accounts with no portal to route to are served the admin app in-place,
  // scoped to their own region — but only when there's no redirect target and no chooser.
  const useScopedApp =
    onGenericHost && !targetRegion && accessibleRegions.length <= 1 && !!scopedRegion;
  const scopedReady = useScopedApp && getScopedRegionOverride() === scopedRegion;

  useEffect(() => {
    if (!onGenericHost) return;
    // While the profile is loading this render can't tell "no scoped region" from
    // "not loaded yet". Clearing here would wipe the pin written just before the
    // one-time reload below, turning it into an infinite reload loop — the post-
    // reload mount always passes through a loading render first.
    if (isLoading) return;
    if (targetRegion) {
      clearScopedRegionOverride();
      window.location.replace(getRegionAdminUrl(targetRegion));
      return;
    }
    if (useScopedApp && scopedRegion && getScopedRegionOverride() !== scopedRegion) {
      // Pin the region, then reload once so module-load derivations (API base, i18n) pick it up.
      setScopedRegionOverride(scopedRegion);
      window.location.reload();
    } else if (!useScopedApp) {
      clearScopedRegionOverride();
    }
  }, [onGenericHost, isLoading, targetRegion, useScopedApp, scopedRegion]);

  if (!onGenericHost) {
    return <>{children}</>;
  }

  // Loading the profile, or a redirect is in flight.
  if (isLoading || targetRegion) {
    return <Loader />;
  }

  if (isError || !user) {
    return (
      <div style={wrapStyle}>
        <div style={cardStyle}>
          <h2 style={{ margin: "0 0 8px" }}>Couldn't load your account</h2>
          <p style={{ color: "#555", margin: 0 }}>
            We couldn't reach your Premier Card Grading profile. Please sign in
            again or contact an administrator.
          </p>
        </div>
      </div>
    );
  }

  // No single home portal: let the user choose among their regional dashboards.
  if (accessibleRegions.length > 1) {
    return (
      <div style={wrapStyle}>
        <div style={cardStyle}>
          <h2 style={{ margin: "0 0 8px" }}>Choose a dashboard</h2>
          <p style={{ color: "#555", margin: "0 0 20px" }}>
            Your account has access to multiple regions. Select one to continue.
          </p>
          {accessibleRegions.map((region) => (
            <button
              key={region}
              type="button"
              style={buttonStyle}
              onClick={() => window.location.replace(getRegionAdminUrl(region))}
            >
              {regionLabel(region)}
            </button>
          ))}
        </div>
      </div>
    );
  }

  // Dev/test/unlinked: render the scoped admin app in-place (children) once the region is pinned;
  // a Loader covers the one-time reload so no region-scoped request fires against the fallback.
  if (useScopedApp) {
    return scopedReady ? <>{children}</> : <Loader />;
  }

  // No region with a portal is assigned (e.g. a franchise region whose portal isn't live yet).
  return (
    <div style={wrapStyle}>
      <div style={cardStyle}>
        <h2 style={{ margin: "0 0 8px" }}>No regional dashboard assigned</h2>
        <p style={{ color: "#555", margin: 0 }}>
          Your account isn't linked to a regional Premier Card Grading dashboard
          yet. Please contact an administrator to be assigned a region.
        </p>
      </div>
    </div>
  );
};

export default RegionLandingGuard;
